import { ImageInput } from "@/components/image-input/image-input";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Icons } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ChevronDown, ChevronRight } from "lucide-react";
import { memo, useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { PostableWithExtras } from "./types";

interface ConfigureExtrasProps {
  postable: PostableWithExtras;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  onUpdateField: (field: string, value: any) => void;
  initialPostableId: string;
  onClose?: () => void;
  onUpdate?: (updatedPostable: PostableWithExtras) => void;
}

export const ConfigureExtras = memo(function ConfigureExtras({
  postable,
  onUpdateField,
  // biome-ignore lint/correctness/noUnusedVariables: <explanation>
  initialPostableId,
  onClose,
  onUpdate,
}: ConfigureExtrasProps) {
  const [isOpen, setIsOpen] = useState(true);
  // Track the last uploaded image to prevent duplicate uploads
  const lastUploadedImageRef = useRef("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm({
    defaultValues: {
      displayImage: postable.json?.displayImage || "",
      displayTitle: postable.json?.displayTitle || "",
    },
    values: {
      displayImage: postable.json?.displayImage || "",
      displayTitle: postable.json?.displayTitle || "",
    },
  });

  const handleSave = useCallback(() => {
    if (onUpdate) {
      setIsSaving(true);
      // Stage the changes by calling onUpdate with the current postable state
      onUpdate(postable);

      // Close the sheet after a brief delay to show the saving state
      setTimeout(() => {
        setIsSaving(false);
        if (onClose) {
          onClose();
        }
      }, 500);
    }
  }, [onUpdate, postable, onClose]);

  // Handle image upload/change - use useCallback to prevent recreation on each render
  const handleImageChange = useCallback(
    (url: string) => {
      // Skip empty URLs
      if (!url) {
        onUpdateField("displayImage", "");
        form.setValue("displayImage", "");
        lastUploadedImageRef.current = "";
        return;
      }

      // Skip if this is a duplicate upload or we're already uploading
      if (lastUploadedImageRef.current === url || isUploading) {
        console.log("DUPLICATE PREVENTED:", url);
        return;
      }

      // Set uploading flag to prevent multiple simultaneous uploads
      setIsUploading(true);

      lastUploadedImageRef.current = url;

      // Update the field in the parent component
      onUpdateField("displayImage", url);

      // Reset uploading flag after a short delay
      setTimeout(() => {
        setIsUploading(false);
      }, 1000);
    },
    [onUpdateField, form, isUploading],
  );

  // Handle title change - use event memoization to prevent frequent re-renders
  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onUpdateField("displayTitle", newValue);
      form.setValue("displayTitle", newValue);
    },
    [onUpdateField, form],
  );

  const supportedPostableTypes = [
    "basicPost",
    "webLink",
    "event",
    "video",
    "place",
    "contactCard",
    "nativeScreen",
  ];

  const getPostableTypeDescription = (postableType: string) => {
    switch (postableType) {
      case "webLink":
        return "link";
      case "basicPost":
        return "post";
      case "event":
        return "event";
      case "video":
        return "video";
      case "place":
        return "place";
      case "contactCard":
        return "contact card";
      case "nativeScreen":
        return "screen";
      default:
        return "item";
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <div className="flex items-center">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center p-0 gap-1"
          >
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span>Configure Display Options</span>
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="mt-4 space-y-4">
        <div className="text-sm text-muted-foreground">
          Configure how this item appears in the feed
        </div>

        {supportedPostableTypes.includes(postable.postableType) && (
          <>
            <div className="grid gap-2">
              <Label htmlFor="displayImage">Display Image</Label>
              <ImageInput
                form={form}
                field={{
                  name: "displayImage",
                  onChange: handleImageChange,
                }}
                contentType="image/*"
                displayType="card"
              />
              <p className="text-xs text-muted-foreground">
                Override the default image for this{" "}
                {getPostableTypeDescription(postable.postableType)}
              </p>
            </div>

            <div className="grid gap-2 mx-2">
              <Label htmlFor="displayTitle">Display Title</Label>
              <Input
                id="displayTitle"
                placeholder="Custom title"
                value={form.watch("displayTitle")}
                onChange={handleTitleChange}
              />
              <p className="text-xs text-muted-foreground">
                Override the default title for this{" "}
                {getPostableTypeDescription(postable.postableType)}
              </p>
            </div>
          </>
        )}

        <Button
          variant="default"
          className="w-full"
          disabled={isSaving || isUploading}
          onClick={handleSave}
        >
          {isSaving ? (
            <>
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Options"
          )}
        </Button>
      </CollapsibleContent>
    </Collapsible>
  );
});
